import { Box, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/Button/Button';
import CustomSwitch from '../../../../components/Switch/Switch';
import CustomTimePicker from '../../../../components/TimePicker/TimePicker';
import TimeUnitInput from '../../../../components/TimeUnitInput/TimeUnitInput';
import { LANG_TEXT } from '../../../../constants/language';
import { getButtonStyle } from './styles';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedDate: Dayjs | null;
  initialValues: {
    gameStart: string;
    deliveryStart: string;
    preOrder?: boolean;
    sinceAmount?: number;
    sinceUnit?: 'minute' | 'hour' | 'day';
    untilAmount?: number;
    untilUnit?: 'minute' | 'hour' | 'day';
  };
  onConfirm: (data: {
    gameStart: string;
    deliveryStart: string;
    sinceAmount: number;
    sinceUnit: string;
    untilAmount: number;
    untilUnit: string;
    preOrderEnabled?: boolean;
  }) => void;
  error?: string;
}

const GameScheduleEditor: React.FC<Props> = ({
  open,
  onClose,
  selectedDate,
  initialValues,
  onConfirm,
  error,
}) => {
  const [gameStart, setGameStart] = useState(initialValues.gameStart);
  const [deliveryStart, setDeliveryStart] = useState(initialValues.deliveryStart);
  const [switchValue, setSwitchValue] = useState<boolean>(false);
  const [sinceAmount, setSinceAmount] = useState<number>(1);
  const [sinceUnit, setSinceUnit] = useState<'minute' | 'hour' | 'day'>('hour');
  const [untilAmount, setUntilAmount] = useState<number>(2);
  const [untilUnit, setUntilUnit] = useState<'minute' | 'hour' | 'day'>('hour');

  const { t } = useTranslation();

  useEffect(() => {
    setGameStart(initialValues.gameStart);
    setDeliveryStart(initialValues.deliveryStart);
    setSwitchValue(initialValues.preOrder ?? false);
    setSinceAmount(initialValues.sinceAmount ?? 1);
    setSinceUnit(initialValues.sinceUnit ?? 'hour');
    setUntilAmount(initialValues.untilAmount ?? 2);
    setUntilUnit(initialValues.untilUnit ?? 'hour');
  }, [initialValues, open]);

  const { control } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    const baseDate = selectedDate ? selectedDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    if (!gameStart) return;
    const dateTime = dayjs(`${baseDate}T${gameStart}`);
    if (!dateTime.isValid()) return;

    const computed = dateTime.subtract(sinceAmount, sinceUnit);
    if (computed.isValid()) {
      setDeliveryStart(computed.format('HH:mm'));
    }
  }, [gameStart, sinceAmount, sinceUnit, selectedDate]);

  const handleConfirm = () => {
    onConfirm({
      gameStart,
      deliveryStart,
      sinceAmount,
      sinceUnit: sinceUnit,
      untilAmount,
      untilUnit: untilUnit,
      preOrderEnabled: switchValue,
    });
  };

  const handleSwitchChange = (checked: boolean) => {
    setSwitchValue(checked);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" sx={{ '& .MuiDialog-paper': { minWidth: 600, width: 'auto' } }}>
      <DialogContent sx={{ padding: 0 }}>
        <Box
          sx={{
            display: 'flex',
            padding: '12px',
            alignItems: 'flex-start',
            gap: '6px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'stretch', gap: '6px' }}>
            <Typography fontSize={'16px'} fontWeight={'600'} color={'#344054'} lineHeight={'24px'} whiteSpace={'nowrap'}>
              {t(LANG_TEXT.GAME_SCHEDULE)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '40px' }}>
              <Box></Box>

              <Typography fontSize="14px" fontWeight={600} color="#344054" lineHeight="20px" whiteSpace="nowrap" textAlign="right">
                {t(LANG_TEXT.GAME)}
              </Typography>

              <Box sx={{ mt: 2, pt: 5 }}>
                <Typography fontSize="14px" fontWeight={600} color="#344054" lineHeight="20px" whiteSpace="nowrap" textAlign="right">
                  {t(LANG_TEXT.ORDERS_DELIVERABLE)}
                </Typography>
                <Typography variant="caption" display="block" fontSize="12px" color="#475467" lineHeight="20px" whiteSpace="nowrap">
                  {t(LANG_TEXT.IN_RELATION_WITH_GAME_STARTS)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              padding: '12px',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '40px',
              borderRadius: '8px',
              background: 'var(--Gray-100, #F2F4F7)',
            }}
          >
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <CustomSwitch onChange={handleSwitchChange} isChecked={switchValue} />
              <Typography fontSize={16} lineHeight={'24px'} color={'var(--Gray-700, #344054)'}>
                {t(LANG_TEXT.PRE_ORDERS)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  padding: '8px 14px',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  border: '1px solid var(--Gray-300, #D0D5DD)',
                  background: '#FFF',
                  boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.05)',
                }}
              >
                <Typography fontSize="16px" fontWeight={400} color="#101828" lineHeight="24px">
                  {selectedDate ? selectedDate.format('YYYY/MM/DD') : ''}
                </Typography>
              </Box>
              <CustomTimePicker
                title={t(LANG_TEXT.GAME_STARTS)}
                field="gameStart"
                control={control}
                enabled={true}
                day_of_week="MON"
                defaultValue={selectedDate ? `${selectedDate.format('YYYY-MM-DD')}T${gameStart || '00:00'}` : gameStart ? dayjs().format(`YYYY-MM-DDT${gameStart}`) : ''}
                onDataChange={(day, data) => {
                  if (data && data.gameStart) {
                    const timeObj = dayjs(data.gameStart);
                    if (timeObj.isValid()) {
                      const t = timeObj.format('HH:mm');
                      setGameStart(t);
                      try {
                        const computed = timeObj.subtract(sinceAmount, sinceUnit);
                        if (computed.isValid()) {
                          setDeliveryStart(computed.format('HH:mm'));
                        }
                      } catch (e) {
                        // fallback: do nothing
                      }
                    }
                  }
                }}
                compact={true}
                disableTimezone={true}
              />
              <CustomTimePicker
                title={t(LANG_TEXT.DELIVERY_STARTS)}
                field="deliveryStart"
                control={control}
                enabled={false}
                day_of_week="MON"
                key={deliveryStart}
                defaultValue={
                  selectedDate ? `${selectedDate.format('YYYY-MM-DD')}T${deliveryStart || '00:00'}` : deliveryStart ? dayjs().format(`YYYY-MM-DDT${deliveryStart}`) : ''
                }
                onDataChange={() => {}}
                compact={true}
                disableTimezone={true}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', pt: 5 }}>
              <TimeUnitInput
                label1={t(LANG_TEXT.SINCE)}
                typeProp="none"
                defaultValue={sinceAmount}
                defaultUnit={sinceUnit}
                allowDay={false}
                onChange={(next) => {
                  setSinceAmount(next.value);
                  setSinceUnit(next.unit);
                }}
              />

              <Typography fontSize="14px" fontWeight={400} color="#344054" lineHeight="20px" paddingRight="12px">
                {t(LANG_TEXT.BEFORE)}
              </Typography>

              <TimeUnitInput
                label1={t(LANG_TEXT.UNTIL)}
                typeProp="none"
                defaultValue={untilAmount}
                defaultUnit={untilUnit}
                allowDay={false}
                onChange={(next) => {
                  setUntilAmount(next.value);
                  setUntilUnit(next.unit);
                }}
              />

              <Typography fontSize="14px" fontWeight={600} color="#344054" lineHeight="20px" whiteSpace="nowrap">
                {t(LANG_TEXT.LATER)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      {error && (
        <Typography color="error" fontSize="14px" textAlign="center">
          {error}
        </Typography>
      )}
      <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, paddingTop: 1, width: '100%' }}>
        <Box sx={{ display: 'flex', gap: 1, width: 'max-content' }}>
          <Button
            id="cancel_button"
            fullWidth={false}
            title={t(LANG_TEXT.CANCEL)}
            buttonStyle={getButtonStyle('var(--Base-White, #FFF)', 'var(--Gray-300, #D0D5DD)')}
            sxTextStyle={{ color: 'var(--Gray-700, #344054)', fontSize: '16px', fontWeight: 600, lineHeight: '24px' }}
            onClick={onClose}
          />
          <Button
            id="confirm_button"
            fullWidth={false}
            title={t(LANG_TEXT.CONFIRM)}
            buttonStyle={getButtonStyle('#30893C', '#30893C')}
            sxTextStyle={{ color: 'var(--Base-White, #FFF)', fontSize: '16px', fontWeight: 600, lineHeight: '24px' }}
            onClick={() => {
              handleConfirm();
            }}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default GameScheduleEditor;
